/**
 * Task #6477 — per-market keyword homepage titles/descriptions.
 *
 * Single source of truth for the SPA side of the homepage/country-homepage
 * <title> + meta description. scripts/serve-static.mjs carries a mirror
 * (MARKET_ROOT_COPY + HOME_* constants — plain Node ESM cannot import this
 * TS module), and the parity suite
 * `src/__tests__/market-home-seo.parity.test.ts` asserts the prerendered
 * HTML and this module emit IDENTICAL titles/descriptions for every
 * (market × language) combination, so the two copies can never silently
 * drift (same pattern as the hreflang cluster / #6392).
 */
import { MARKETS_BY_CODE } from "@/i18n/markets";
import { getPlatformConfig } from "@/lib/platform/config";

const BAC_NAME = getPlatformConfig("bac").name;

/** Keyword-bearing title/description for the worldwide homepage (`/`). */
export const HOME_DEFAULT_TITLE =
  "Classic & Vintage Car Hire from Private Owners | BookAclassic";
export const HOME_DEFAULT_DESCRIPTION =
  "Peer-to-peer marketplace for hiring classic, vintage and prestige cars from private owners. Wedding cars, self-drive rentals, chauffeured experiences and event hire across the UK, Ireland, mainland Europe, Canada, Australia, New Zealand and South Africa.";

// English prose labels use the definite article where natural English
// requires it ("in the United Kingdom"); the compact title form drops it
// ("Classic Car Hire UK"). Mirrors the serve-static HREFLANG_MARKETS labels.
const EN_LABEL_OVERRIDES: Record<string, string> = {
  uk: "the United Kingdom",
  nl: "the Netherlands",
};
const TITLE_LABEL_OVERRIDES: Record<string, string> = {
  uk: "UK",
};

export interface MarketHomeSeoCopy {
  title: string;
  description: string;
}

// Per-language copy templates. `country` is the prose country label,
// `titleCountry` the compact title form. KEEP IN SYNC with MARKET_ROOT_COPY
// in scripts/serve-static.mjs (title + description fields).
const MARKET_HOME_COPY: Record<string, (country: string, titleCountry: string) => MarketHomeSeoCopy> = {
  en: (country, titleCountry) => ({
    title: titleCountry === "UK"
      ? `Classic Car Hire UK: Self-Drive & Chauffeured | ${BAC_NAME}`
      : `Classic Car Hire ${titleCountry}: Vintage, Wedding & Self-Drive | BookAclassic`,
    description: titleCountry === "UK"
      ? "Hire a classic, vintage or American car direct from private owners across the UK. Self-drive days out, chauffeured wedding cars, proms, film work and photoshoots."
      : `Hire classic, vintage and prestige cars from private owners in ${country}. Self-drive rentals, chauffeured experiences and wedding car hire on BookAclassic.`,
  }),
  de: (country) => ({
    title: `Oldtimer mieten in ${country} | BookAclassic`,
    description: `Mieten Sie Oldtimer, Youngtimer und klassische Fahrzeuge von privaten Besitzern in ${country}. Selbstfahrer-Vermietung, Chauffeurservice und Hochzeitsautos auf BookAclassic.`,
  }),
  fr: (country) => ({
    title: `Location de voitures classiques et de collection : ${country} | BookAclassic`,
    description: `Louez des voitures classiques, anciennes et de prestige auprès de propriétaires privés (${country}). Location avec ou sans chauffeur et voitures de mariage sur BookAclassic.`,
  }),
  es: (country) => ({
    title: `Alquiler de coches clásicos y de época: ${country} | BookAclassic`,
    description: `Alquila coches clásicos, antiguos y de prestigio de propietarios particulares (${country}). Alquiler sin conductor, con chófer y coches de boda en BookAclassic.`,
  }),
  it: (country) => ({
    title: `Noleggio auto d'epoca e classiche: ${country} | BookAclassic`,
    description: `Noleggia auto classiche, d'epoca e di prestigio da proprietari privati (${country}). Noleggio con o senza conducente e auto per matrimoni su BookAclassic.`,
  }),
  pt: (country) => ({
    title: `Aluguer de carros clássicos e vintage: ${country} | BookAclassic`,
    description: `Alugue carros clássicos, antigos e de prestígio de proprietários particulares (${country}). Aluguer sem condutor, com motorista e carros de casamento na BookAclassic.`,
  }),
  da: (country) => ({
    title: `Udlejning af klassiske biler og veteranbiler i ${country} | BookAclassic`,
    description: `Lej klassiske biler, veteranbiler og prestigebiler af private ejere i ${country}. Selvkørende udlejning, chaufførservice og bryllupsbiler på BookAclassic.`,
  }),
  sv: (country) => ({
    title: `Hyr klassiska bilar och veteranbilar i ${country} | BookAclassic`,
    description: `Hyr klassiska bilar, veteranbilar och prestigebilar av privata ägare i ${country}. Hyr med eller utan chaufför samt bröllopsbilar på BookAclassic.`,
  }),
};

/**
 * Resolve the keyword homepage title/description for a market root
 * (`/uk`, `/dk`, `/ca/fr`, …). Returns null for the worldwide root, the US
 * sister-brand tree, and unknown markets — callers fall back to
 * HOME_DEFAULT_TITLE / HOME_DEFAULT_DESCRIPTION.
 */
export function marketHomeSeo(
  market: string | null | undefined,
  language?: string | null,
): MarketHomeSeoCopy | null {
  if (!market) return null;
  const code = market.toLowerCase();
  if (code === "global") return null;
  const meta = MARKETS_BY_CODE[code];
  if (!meta) return null;
  const lang = language && meta.languages.includes(language) ? language : meta.defaultLanguage;
  const enLabel = EN_LABEL_OVERRIDES[code] || meta.label;
  const country = lang === "en" ? enLabel : meta.nativeLabel;
  const titleCountry = TITLE_LABEL_OVERRIDES[code] || enLabel.replace(/^the /, "");
  return (MARKET_HOME_COPY[lang] || MARKET_HOME_COPY.en)(country, titleCountry);
}
